/**
 * Company Info Card Component
 */
import { 
  Building2, MapPin, Briefcase, User, Phone, Mail, 
  UserPlus, Pencil, Trash2, X, DollarSign, Database,
  TrendingUp, Banknote, Users
} from "lucide-react";

export const CompanyInfoCard = ({
  company,
  canAssignAgents,
  isAdmin,
  onAssignClick,
  onUnassign,
  onEditClick,
  onDeleteClick,
  assigning
}) => {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden animate-fadeIn sticky top-24">
      <div className="p-5">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-14 h-14 rounded-xl bg-[#1B3F8F]/10 flex items-center justify-center">
            <Building2 size={28} className="text-[#1B3F8F]" />
          </div>
          <div>
            <a 
              href={`https://www.google.com/search?q=${encodeURIComponent(company?.name || '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-lg font-semibold text-gray-900 tracking-tight hover:text-[#1B3F8F] hover:underline flex items-center gap-1"
              data-testid="company-name-google-link"
            >
              {company?.name}
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
            {company?.oib && (
              <p className="text-xs text-gray-400">ID: {company.oib}</p>
            )}
          </div>
        </div>
        
        <div className="space-y-4">
          {/* Address */}
          {company?.address && (
            <InfoRow 
              icon={<MapPin size={18} className="text-gray-500" />}
              label="Adresa"
              value={
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(company.address + (company.city ? ', ' + company.city : ''))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1B3F8F] font-medium hover:underline flex items-center gap-1"
                  data-testid="address-google-maps-link"
                >
                  {company.address}
                  <ExternalLinkIcon />
                </a>
              }
            />
          )}
          
          {/* Industry */}
          <InfoRow 
            icon={<Briefcase size={18} className="text-gray-500" />}
            label="Industrija"
            value={<span className="text-gray-900 font-medium">{company?.industry}</span>}
          />
          
          {/* Source */}
          <InfoRow 
            icon={<Database size={18} className="text-gray-500" />}
            label="Izvor"
            value={
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                company?.source === 'AI pretraga' ? 'bg-purple-100 text-purple-700' :
                company?.source === 'JN' ? 'bg-blue-100 text-blue-700' :
                company?.source === 'Import' ? 'bg-amber-100 text-amber-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {company?.source || 'Nepoznato'}
              </span>
            }
          />
          
          {/* Estimated Value */}
          <InfoRow 
            icon={<DollarSign size={18} className="text-emerald-600" />}
            label="Proc. vrijednost akvizicije"
            value={
              company?.estimated_value ? (
                <span className="text-emerald-600 font-semibold">
                  {company.estimated_value.toLocaleString('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} KM
                </span>
              ) : (
                <span className="text-gray-400 italic text-xs">Nije procijenjeno</span>
              )
            }
            iconBg="bg-emerald-50"
          />
          
          {/* Revenue */}
          {company?.revenue != null && (
            <InfoRow 
              icon={<TrendingUp size={18} className="text-blue-600" />}
              label="Prihod"
              value={
                <span className="text-blue-600 font-semibold">
                  {company.revenue.toLocaleString('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} KM
                </span>
              }
              iconBg="bg-blue-50"
            />
          )}
          
          {/* Net Profit */}
          {company?.net_profit != null && (
            <InfoRow 
              icon={<Banknote size={18} className="text-green-600" />}
              label="Neto dobit"
              value={
                <span className={`font-semibold ${company.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {company.net_profit.toLocaleString('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} KM
                </span>
              }
              iconBg="bg-green-50"
            />
          )}
          
          {/* Employee Count */}
          {company?.employee_count != null && (
            <InfoRow 
              icon={<Users size={18} className="text-purple-600" />}
              label="Broj zaposlenih"
              value={<span className="text-purple-600 font-semibold">{company.employee_count}</span>}
              iconBg="bg-purple-50"
            />
          )}
          
          {/* Director */}
          {company?.director_name && (
            <InfoRow 
              icon={<User size={18} className="text-gray-500" />}
              label="Direktor"
              value={<DirectorLinks name={company.director_name} />}
            />
          )}
          
          {/* Phone */}
          {company?.phone && (
            <InfoRow 
              icon={<Phone size={18} className="text-[#1B3F8F]" />}
              label="Telefon"
              value={
                <a href={`tel:${company.phone}`} className="text-[#1B3F8F] font-medium hover:underline">
                  {company.phone}
                </a>
              }
              iconBg="bg-blue-50"
            />
          )}
          
          {/* Email */}
          {company?.email && (
            <InfoRow 
              icon={<Mail size={18} className="text-[#1B3F8F]" />}
              label="Email"
              value={
                <a href={`mailto:${company.email}`} className="text-[#1B3F8F] font-medium hover:underline truncate block max-w-[180px]">
                  {company.email}
                </a>
              }
              iconBg="bg-blue-50"
            />
          )}
          
          {/* Assigned Agent */}
          {canAssignAgents && (
            <div className="pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Dodjeljeno</p>
                  {company?.assigned_to_name ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{company.assigned_to_name}</span>
                      <button
                        onClick={onUnassign}
                        disabled={assigning}
                        className="text-red-500 hover:text-red-600 text-xs"
                        title="Oduzmi od agenta"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">Nije dodijeljeno</span>
                  )}
                </div>
                <button
                  onClick={onAssignClick}
                  className="p-2 bg-[#1B3F8F]/10 rounded-lg text-[#1B3F8F] hover:bg-[#1B3F8F]/20"
                  title="Dodijeli agentu"
                >
                  <UserPlus size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Action buttons */}
        <div className="mt-5 pt-4 border-t border-gray-100 flex gap-2">
          <button
            onClick={onEditClick}
            className="flex-1 py-2 px-3 bg-gray-100 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-200 flex items-center justify-center gap-2"
          >
            <Pencil size={14} /> Uredi
          </button>
          {isAdmin && (
            <button
              onClick={onDeleteClick}
              className="py-2 px-3 bg-red-50 rounded-lg text-red-600 text-sm font-medium hover:bg-red-100 flex items-center justify-center gap-2"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper components
const InfoRow = ({ icon, label, value, iconBg = "bg-gray-50" }) => (
  <div className="flex items-center gap-3 text-sm">
    <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center`}>
      {icon}
    </div>
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      {value}
    </div>
  </div>
);

const ExternalLinkIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

const DirectorLinks = ({ name }) => (
  <div className="flex items-center gap-2">
    <a 
      href={`https://www.google.com/search?q=${encodeURIComponent(name)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[#1B3F8F] font-medium hover:underline flex items-center gap-1"
      data-testid="director-google-link"
      title="Pretraži na Google"
    >
      {name}
      <ExternalLinkIcon />
    </a>
    <a 
      href={`https://www.linkedin.com/search/results/all/?keywords=${encodeURIComponent(name)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[#0A66C2] hover:text-[#004182] transition-colors"
      data-testid="director-linkedin-link"
      title="Pretraži na LinkedIn"
    >
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    </a>
  </div>
);
